import React, { useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ComposedChart,
} from "recharts";

// ---- YEARLY DATA ----
const yearlyData = {
  revenueEbitda: [
    { period: "2022", Revenue: 22.3, EBITDA: 7.6 },
    { period: "2023", Revenue: 49.1, EBITDA: 16.2 },
    { period: "2024", Revenue: 155.7, EBITDA: 74.1 },
  ],
  ebitGp: [
    { period: "2022", EBIT: 5.4, GP: 7.9 },
    { period: "2023", EBIT: 13.9, GP: 16.3 },
    { period: "2024", EBIT: 71.4, GP: 76.2 },
  ],
  roe: [
    { period: "2022", ROE: 19.4 },
    { period: "2023", ROE: 44.5 },
    { period: "2024", ROE: 162.7 },
  ],
  roa: [
    { period: "2022", ROA: 12.5 },
    { period: "2023", ROA: 22.6 },
    { period: "2024", ROA: 62.7 },
  ],
  liquidity: [
    { period: "2022", Current: 3.3, Quick: 0.5 },
    { period: "2023", Current: 1.6, Quick: 0.4 },
    { period: "2024", Current: 1.4, Quick: 0.7 },
  ],
  debtEquity: [
    { period: "2022", DebtToEquity: 0.0 },
    { period: "2023", DebtToEquity: 0.4 },
    { period: "2024", DebtToEquity: 0.2 },
  ],
  efficiency: [
    { period: "2022", WC: 12.9, NWC: 13.6 },
    { period: "2023", WC: 15.3, NWC: 33.9 },
    { period: "2024", WC: 36.2, NWC: 72.4 },
  ],
  nwcToRevenue: [
    { period: "2022", NWCtoRevenue: 61.1 },
    { period: "2023", NWCtoRevenue: 69.0 },
    { period: "2024", NWCtoRevenue: 46.5 },
  ],
};

// ---- QUARTERLY DATA ----
const quarterlyData = [
  { period: "Q1 2025", Revenue: 37.2, GP: 14.5, GP_Margin: 39.0, EBIT: 13.2, EBIT_Margin: 35.4, EBITDA: 14.4, EBITDA_Margin: 38.7 },
  { period: "Q2 2025", Revenue: 23.9, GP: 11.1, GP_Margin: 46.4, EBIT: 9.7, EBIT_Margin: 40.8, EBITDA: 11.0, EBITDA_Margin: 46.0 },
  { period: "Q1 2024", Revenue: 27.0, GP: 12.5, GP_Margin: 46.2, EBIT: 11.3, EBIT_Margin: 41.9, EBITDA: 11.9, EBITDA_Margin: 44.3 },
  { period: "Q2 2024", Revenue: 36.3, GP: 19.9, GP_Margin: 54.8, EBIT: 19.1, EBIT_Margin: 52.5, EBITDA: 19.7, EBITDA_Margin: 54.3 },
];

const FILES = [
  { label: "FS 2022",     href: "/assets/pdf/FS-2022.pdf" },
  { label: "FS 2023",     href: "/assets/pdf/FS-2023.pdf" },
  { label: "FS 2024",     href: "/assets/pdf/FS-2024.pdf" },
  { label: "FS Q1-2025",  href: "/assets/pdf/FS-Q1-2025.pdf" },
  { label: "FS H1-2025",  href: "/assets/pdf/FS-H1-2025.pdf" },
];

function DownloadIcon(props) {
  return (
    <svg className="dl-icon" viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path
        d="M12 3v10m0 0l4-4m-4 4l-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SectionHeader({ title }) {
  return <h2 className="section-title">{title} (SAR Millions)</h2>;
}

export default function InvestorDashboard() {
  const page = "dashboard";
  const [tab, setTab] = useState("yearly");
  const BLUE = "#1d4ed8";
  const GREEN = "#16a34a";
  const MAROON = "#800033";
  const PURPLE = "#7c3aed";
  const GOLD = "#d97706";
  const ORANGE = "#f97316";

  const box = {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#fff",
    padding: 16,
    marginBottom: 20,
  };

  const kpiCard = (title, value) => (
    <div style={{ background: "#fff", padding: "20px 25px", borderRadius: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.05)", border: "1px solid #e5e7eb" }}>
      <p style={{ color: "#6b7280", fontSize: "13px", fontWeight: 500 }}>{title}</p>
      <h2 style={{ fontSize: "28px", fontWeight: 800 }}>SAR {value}M</h2>
    </div>
  );
  return (
    <>
      <section className={`hero ${page}`}>
          <h1>Dashboard</h1>
      </section>
      <section className="dl-section">
        <div className="dl-container">
          <div className="dl-grid">
            {FILES.map(({ label, href }, i) => (
              <a
                className="dl-btn"
                href={href}
                download
                aria-label={`Download ${label}`}
                key={i}
              >
                <span>{label}</span>
                <DownloadIcon />
              </a>
            ))}
          </div>
        </div>
      </section>      

      <div style={{ padding: 30, fontFamily: "Inter, sans-serif", background: "#f9fafb" }}>
        <div style={{ marginBottom: 10 }}>
          <h1 style={{ fontWeight: 700, fontSize: "28px", marginBottom: 5 }}>Investor Dashboard</h1>
          <p style={{ color: "#6b7280", fontSize: "15px", marginTop: 0 }}>Comparison across years and quarters.</p>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 25 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setTab("yearly")} style={{ padding: "8px 18px", borderRadius: 20, fontWeight: 600, border: tab === "yearly" ? "none" : "1px solid #ccc", background: tab === "yearly" ? "black" : "white", color: tab === "yearly" ? "white" : "black", cursor: "pointer" }}>Yearly</button>
            <button onClick={() => setTab("quarterly")} style={{ padding: "8px 18px", borderRadius: 20, fontWeight: 600, border: tab === "quarterly" ? "none" : "1px solid #ccc", background: tab === "quarterly" ? "black" : "white", color: tab === "quarterly" ? "white" : "black", cursor: "pointer" }}>Quarterly</button>
          </div>
        </div>

        {tab === "yearly" ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 25, marginBottom: 40 }}>
              {kpiCard("2024 REVENUE", 155.7)}
              {kpiCard("2024 GROSS PROFIT", 76.17)}
              {kpiCard("2024 EBITDA", 74.1)}
              {kpiCard("2024 EBIT", 71.4)}
            </div>

            <div style={box}><h4>Revenue & EBITDA (SAR Millions)</h4><ResponsiveContainer width="100%" height={350}><LineChart data={yearlyData.revenueEbitda}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" /><YAxis /><Tooltip /><Legend /><Line dataKey="Revenue" stroke={BLUE} strokeWidth={3} /><Line dataKey="EBITDA" stroke={GREEN} strokeWidth={3} /></LineChart></ResponsiveContainer></div>

            <div style={box}><h4>EBIT & Gross Profit (SAR Millions)</h4><ResponsiveContainer width="100%" height={350}><LineChart data={yearlyData.ebitGp}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" /><YAxis /><Tooltip /><Legend /><Line dataKey="EBIT" stroke={MAROON} strokeWidth={3} /><Line dataKey="GP" stroke={PURPLE} strokeWidth={3} /></LineChart></ResponsiveContainer></div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}><div style={box}><h4>ROE %</h4><ResponsiveContainer width="100%" height={300}><LineChart data={yearlyData.roe}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" /><YAxis /><Tooltip /><Legend /><Line dataKey="ROE" stroke={BLUE} strokeWidth={3} /></LineChart></ResponsiveContainer></div><div style={box}><h4>ROA %</h4><ResponsiveContainer width="100%" height={300}><LineChart data={yearlyData.roa}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" /><YAxis /><Tooltip /><Legend /><Line dataKey="ROA" stroke={PURPLE} strokeWidth={3} /></LineChart></ResponsiveContainer></div></div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}><div style={box}><h4>Liquidity Ratios</h4><ResponsiveContainer width="100%" height={300}><BarChart data={yearlyData.liquidity}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" /><YAxis /><Tooltip /><Legend /><Bar dataKey="Current" fill={BLUE} /><Bar dataKey="Quick" fill={GOLD} /></BarChart></ResponsiveContainer></div><div style={box}><h4>Debt to Equity</h4><ResponsiveContainer width="100%" height={300}><LineChart data={yearlyData.debtEquity}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" /><YAxis /><Tooltip /><Legend /><Line dataKey="DebtToEquity" stroke={BLUE} strokeWidth={3} /></LineChart></ResponsiveContainer></div></div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}><div style={box}><h4>Efficiency & Cash Cycle</h4><ResponsiveContainer width="100%" height={300}><BarChart data={yearlyData.efficiency}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" /><YAxis /><Tooltip /><Legend /><Bar dataKey="NWC" fill={MAROON} /><Bar dataKey="WC" fill={BLUE} /></BarChart></ResponsiveContainer></div><div style={box}><h4>NWC to Revenue</h4><ResponsiveContainer width="100%" height={300}><LineChart data={yearlyData.nwcToRevenue}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="period" /><YAxis /><Tooltip /><Legend /><Line dataKey="NWCtoRevenue" stroke={GREEN} strokeWidth={3} /></LineChart></ResponsiveContainer></div></div>
          </>
        ) : (
          <>
            <div style={box}>
              <h4>EBITDA, Revenue & Margin (SAR Millions / %)</h4>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={quarterlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis yAxisId="left" label={{ value: "SAR Millions", angle: -90, position: "insideLeft" }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value: "EBITDA Margin %", angle: -90, position: "insideRight" }} />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="Revenue" fill={BLUE} name="Revenue" />
                  <Bar yAxisId="left" dataKey="EBITDA" fill={PURPLE} name="EBITDA" />
                  <Line yAxisId="right" type="monotone" dataKey="EBITDA_Margin" stroke={GREEN} strokeWidth={3} dot={{ r: 5 }} name="EBITDA Margin %" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div style={box}>
              <h4>EBIT, Revenue & Margin (SAR Millions / %)</h4>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={quarterlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis yAxisId="left" label={{ value: "SAR Millions", angle: -90, position: "insideLeft" }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value: "EBIT Margin %", angle: -90, position: "insideRight" }} />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="Revenue" fill={BLUE} name="Revenue" />
                  <Bar yAxisId="left" dataKey="EBIT" fill={MAROON} name="EBIT" />
                  <Line yAxisId="right" type="monotone" dataKey="EBIT_Margin" stroke={ORANGE} strokeWidth={3} dot={{ r: 5 }} name="EBIT Margin %" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div style={box}>
              <h4>Revenue, Gross Profit & Margin (SAR Millions / %)</h4>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={quarterlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis yAxisId="left" label={{ value: "SAR Millions", angle: -90, position: "insideLeft" }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value: "GP Margin %", angle: -90, position: "insideRight" }} />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="Revenue" fill={BLUE} name="Revenue" />
                  <Bar yAxisId="left" dataKey="GP" fill={GREEN} name="Gross Profit" />
                  <Line yAxisId="right" type="monotone" dataKey="GP_Margin" stroke={ORANGE} strokeWidth={3} dot={{ r: 5 }} name="Gross Margin %" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
    </>
  );
}
